<template>
    <div class="bco-trace">
        <div v-if="isCollapsed" class="collapsed-toggle" @click="expand">
            <span>Agent Thought Trace</span>
            <q-icon name="expand_more" size="14px" class="q-ml-xs"/>
        </div>

        <template v-else>
            <div class="trace-header q-mb-sm" :class="{ 'is-clickable': hasFinished }" @click="collapse">
                <span>{{ header }}</span>
                <q-icon v-if="hasFinished" name="expand_less" size="14px" class="q-ml-xs"/>
            </div>

            <div v-for="(step, index) in visibleSteps" :key="index" class="step-container">
                <div class="line-gutter">
                    <div class="line-path q-mt-xs" v-if="index < visibleSteps.length - 1 || (step.is_expanded && step.details.length)"></div>
                    <div class="icon-node"></div>
                </div>
                <div class="step-content">
                    <div class="step-header" @click="toggle(index)" :style="`padding-bottom: ${step.is_expanded || index === visibleSteps.length - 1 ? '0' : '10'}px;`">
                        <div class="flex items-center">
                            <span class="step-name">{{ step.step_name }}</span>
                            <q-icon v-if="step.details.length" name="expand_more" size="14px" class="q-ml-xs step-chevron" :class="{ 'rotate180': step.is_expanded }"/>
                        </div>
                    </div>
                    <div v-if="step.is_expanded && step.details.length" class="details-section" :style="`padding-bottom: ${index < visibleSteps.length - 1 ? '6' : '0'}px;`">
                        <div v-for="(detail, detailIndex) in step.details" :key="detailIndex" class="detail-item">
                            <div class="detail-icon-node"></div>
                            <div class="detail-content">
                                <div v-for="(line, lineIndex) in String(detail).split('\n')" :key="lineIndex" v-html="markdownToHtml(line)" class="detail-text"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <q-spinner-dots v-if="!hasFinished" size="20px" color="secondary" class="q-ml-xs q-mt-xs"/>
        </template>
    </div>
</template>

<script>
const MIN_REVEAL_DELAY = 480;
const MAX_REVEAL_DELAY = 720;

export default {
    name: "bco-thought-trace",

    // `reveal` fires on every step name and every detail, so the panel around the trace
    // can follow the content as it grows past the fold.
    emits: ["complete", "reveal"],

    props: {
        // The steps to reveal. When `live` is set this grows as the parent polls the agent.
        data: {
            type: Array,
            required: true,
            default: () => [],
        },
        // True while the agent is still running, so the trace holds the spinner and waits
        // for more steps instead of folding away once it has revealed everything it has.
        live: {
            type: Boolean,
            default: false,
        },
        // Once the reveal has played for a BCO the parent turns this off, so reopening the
        // card lands straight on the finished, collapsed trace instead of playing it again.
        animate: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            visibleSteps: [],
            hasFinished: false,
            isCollapsed: false,
            revealedCount: 0,
            // Only one reveal loop may run: `data` growing mid-reveal has to extend the
            // current pass rather than start a second one that replays the same steps.
            isRevealing: false,
            runToken: 0,
        };
    },

    computed: {
        header() {
            return this.data.find(step => step?.header)?.header || "Agent thought trace";
        },
    },

    watch: {
        data: {
            handler() {
                this.pump();
            },
            immediate: true,
        },

        live() {
            this.pump();
        },
    },

    beforeUnmount() {
        this.runToken += 1;
    },

    methods: {
        // Reveals whatever has not been shown yet, one step name or one detail at a time.
        // Returns while the agent is still running so the next batch of steps can resume it.
        async pump() {
            if (this.isRevealing || this.hasFinished) {
                return;
            }

            // A trace that has already been watched is filled in at once, so it lands on the
            // finished, collapsed toggle without replaying a reveal the user has seen.
            if (!this.animate) {
                this.revealEverything();
                if (!this.live) {
                    this.finish(this.runToken);
                }
                return;
            }

            this.isRevealing = true;
            const token = this.runToken;
            try {
                while (token === this.runToken) {
                    if (this.revealNextDetail() || this.revealNextStep()) {
                        this.$emit("reveal");
                        await new Promise(res => setTimeout(res, this.revealDelay()));
                        continue;
                    }

                    if (this.live) {
                        return;
                    }

                    this.finish(token);
                    return;
                }
            } finally {
                this.isRevealing = false;
            }
        },

        revealEverything() {
            let revealed = true;
            while (revealed) {
                revealed = this.revealNextDetail() || this.revealNextStep();
            }
        },

        // The details belong to the step already on screen, so they are filled in one by
        // one before the next step name is allowed to appear.
        revealNextDetail() {
            const index = this.revealedCount - 1;
            const step = this.visibleSteps[index];
            const pending = this.pendingDetails(index);
            if (!step || !pending.length) {
                return false;
            }
            step.details.push(pending[0]);
            return true;
        },

        revealNextStep() {
            if (this.revealedCount >= this.data.length) {
                return false;
            }
            const step = this.data[this.revealedCount];
            this.revealedCount += 1;
            this.visibleSteps.push({
                step_name: step?.step_name,
                details: [],
                is_expanded: true,
            });
            return true;
        },

        pendingDetails(index) {
            const shown = this.visibleSteps[index]?.details?.length || 0;
            return (this.data[index]?.details || []).slice(shown);
        },

        // The trailing delay of the last reveal doubles as the time to read it before
        // everything folds away into the toggle.
        finish(token) {
            if (token !== this.runToken || this.hasFinished) {
                return;
            }
            this.hasFinished = true;
            this.collapse();
            this.$emit("complete");
        },

        revealDelay() {
            return MIN_REVEAL_DELAY + Math.random() * (MAX_REVEAL_DELAY - MIN_REVEAL_DELAY);
        },

        expand() {
            this.isCollapsed = false;
        },

        collapse() {
            if (!this.hasFinished) {
                return;
            }
            this.visibleSteps.forEach(step => {
                step.is_expanded = false;
            });
            this.isCollapsed = true;
        },

        toggle(index) {
            const step = this.visibleSteps[index];
            if (step.details.length) {
                step.is_expanded = !step.is_expanded;
            }
        },

        markdownToHtml(markdown) {
            markdown = markdown.replace(/\*\*(.*?)\*\*/g, '<b style="font-weight: 600; font-size: 11px;">$1</b>');
            markdown = markdown.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<i>$1</i>');
            markdown = markdown.replace(/^- (.*$)/gm, '<li style="margin-left:14px; font-size: 10px; line-height: 16px;">$1</li>');
            return markdown;
        },
    },
};
</script>

<style scoped>
.bco-trace {
    width: 100%;
    min-width: 0;
}

.collapsed-toggle {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 11px;
    font-weight: 600;
    color: #424242;
}

.collapsed-toggle:hover {
    color: #1f1f1f;
}

.trace-header {
    display: flex;
    align-items: center;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #424242;
}

.trace-header.is-clickable {
    cursor: pointer;
}

.trace-header.is-clickable:hover {
    color: #1f1f1f;
}

.step-container {
    display: flex;
    position: relative;
    width: 100%;
    min-width: 0;
}

.line-gutter {
    flex-shrink: 0;
    width: 16px;
    position: relative;
    display: flex;
    justify-content: center;
}

.line-path {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background-image: linear-gradient(to bottom, #f3b699 40%, transparent 0%);
    background-size: 1px 6px;
    background-repeat: repeat-y;
}

.icon-node {
    width: 7px;
    height: 7px;
    background-color: #ff4e00;
    border-radius: 50%;
    margin-top: 4px;
    z-index: 1;
}

.step-content {
    flex-grow: 1;
    min-width: 0;
}

.step-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    min-height: 16px;
}

.step-name {
    font-size: 11px;
    font-weight: 600;
    color: #424242;
}

.step-header:hover .step-name,
.step-header:hover .step-chevron {
    color: #1f1f1f;
}

.step-chevron {
    color: #757575;
    transition: transform 0.15s ease;
}

.rotate180 {
    transform: rotate(180deg);
}

.details-section {
    margin-top: 4px;
}

.detail-item {
    display: flex;
    align-items: flex-start;
    margin-left: 2px;
    margin-top: 6px;
    min-width: 0;
}

.detail-icon-node {
    flex-shrink: 0;
    width: 5px;
    height: 5px;
    background-color: #f5966e;
    border-radius: 50%;
    margin-right: 8px;
    margin-top: 5px;
}

.detail-content {
    min-width: 0;
    line-height: 1.5;
    color: #5f5f5f;
}

.detail-text {
    font-size: 10px;
    line-height: 16px;
    overflow-wrap: anywhere;
}
</style>
